import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
  input,
  output,
} from '@angular/core';
import { ModalService } from 'src/app/_services/modal.service';
import { IconNames } from '../icons/icon-names';
import lottie from 'lottie-web';
import { ButtonComponent } from '../buttons/button/button.component';
import { MatIcon } from '@angular/material/icon';
import { SvgTemplateComponent } from '../svg-template/svg-template.component';

@Component({
    selector: 'app-modal',
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.scss'],
    imports: [SvgTemplateComponent, MatIcon, ButtonComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly modalService = inject(ModalService);

  private readonly element: HTMLElement;

  logo = IconNames.LogoColourSmall as string;

  /** Modal id (required) */
  id = input<string>();

  /** Modal title (required) */
  titleText = input<string>();

  /** Primary modal text (optional) */
  primaryText = input<string | undefined>(undefined);

  /** Secondary modal text (optional) */
  secondaryText = input<string | undefined>(undefined);

  /** Does the modal have an animation? (optional)*/
  animationType = input<'spinner' | 'test-tubes' | 'lock' | ''>('');

  /** Modal style  (optional, default: 'default') */
  modalStyle = input<'default' | 'success' | 'error' | 'warning'>('default');

  /** Controls whether one or two action buttons are rendered. 'double-button' shows a secondary button alongside the primary 
   * (optional, default:'single-button') 
   **/
  buttonLayout = input<'single-button' | 'double-button'>('single-button');

  /** Primary button text (required) */
  primaryButtonText = input<string>();

  /** Primary button id (required) */
  primaryButtonId = input<string>();

  /** Secondary button text */
  secondaryButtonText = input<string | undefined>(undefined);

  /** Secondary button id (optional) */
  secondaryButtonId = input<string | undefined>(undefined);

  /** Is the modal processing (optional, default: false) */
  isProcessing = input<boolean>(false);

  /** Has the modal finished processing (optional, default: false) */
  hasCompleted = input<boolean>(false);

  /** Display logo (optional, default: false) */
  displayLogo = input<boolean>(false);

  /** Whether user can close modal (optional, default: true) */
  allowClose = input<boolean>(true);

  /** Output event for clicked button on modal */
  modalButtonClicked = output<string>();

  /**
   * @param {ElementRef} animationContainer Reference to html element containing the animation.
   * @required
   */
  @ViewChild('lottieContainer') animationContainer: ElementRef<HTMLElement>;

  constructor() {
    const el = this.el;
    this.element = el.nativeElement;
  }

  ngOnInit(): void {
  if (this.allowClose()) {
      // Close modal on background click
      this.element.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.className === 'modal--container') {
          this.close();
        }
      });
    }

    // add self (this modal instance) to the modal service so it's accessible from controllers
    this.modalService.add(this);
  }

  ngAfterViewInit(): void {
    if (this.animationType() === 'test-tubes' || this.animationType() === 'lock') {
      this.initAnimation();
    }
  }

  buttonEventHandler(valueEmitted: string): void {
    this.modalButtonClicked.emit(valueEmitted);
  }

  initAnimation(): void {
    const animation = lottie.loadAnimation({
      container: this.animationContainer.nativeElement, // the dom element that will contain the animation
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: `../../assets/lottie/${this.animationType()}.json`, // the path to the animation json
    });

    animation.play();
  }

  // open modal
  open(): void {
    this.element.style.display = 'block';
  }

  // close modal
  close(): void {
    // Reset valdiation text value and close modal
    this.element.style.display = 'none';
  }

  // Remove self from modal service when component is destroyed
  ngOnDestroy(): void {
    this.modalService.remove(this.id());
    this.element.remove();
  }
}
